import { FunctionComponent, useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import useAsync from 'react-use/lib/useAsync';
import useAsyncFn from 'react-use/lib/useAsyncFn';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { Progress, ResponseErrorPanel } from '@backstage/core-components';
import { Grid, TextAreaField, TextField } from '@backstage/ui';
import {
  DropdownField,
  isPluginEnabled,
  RequiredStar,
  StepTimeline,
  SummaryValue,
  useDropdownField,
} from '@internal/plugin-shared-react';
import { sonarqubeApiRef } from '../../../api/sonarqube-api';
import { SonarQubeServiceGroup } from '../../../api/service-groups/types';
import {
  isValidProjectDescription,
  isValidProjectKey,
  isValidQualityGateName,
  isValidRepositoryName,
  projectKeyPrefix,
} from '../../../utils/projects/keys';
import shared from '../../../styles.module.css';
import styles from './CreateSonarQubeProjectPage.module.css';

export const sonarQubeProjectSteps = [
  'General information',
  'GitHub repository',
  'Quality gate',
  'Summary',
];

/** The current values of a project being updated. */
export interface ExistingSonarQubeProject {
  projectKey: string;
  repositoryName: string;
  projectDescription: string;
  qualityGateName: string;
}

// Form state and the create (or, with `existing`, update) call; the page owns navigation between steps.
export const useSonarQubeProjectForm = (
  serviceGroup: SonarQubeServiceGroup | undefined,
  onDone: () => void,
  existing?: ExistingSonarQubeProject,
) => {
  const sonarqubeApi = useApi(sonarqubeApiRef);
  const qualityGates = useAsync(
    () => sonarqubeApi.listQualityGates(),
    [sonarqubeApi],
  );
  // null until edited, so the key is prefilled with `{applicationKey}_` once the group loads.
  const [keyInput, setProjectKey] = useState<string | null>(
    existing?.projectKey ?? null,
  );
  const [projectDescription, setProjectDescription] = useState(
    existing?.projectDescription ?? '',
  );
  const [repositoryName, setRepositoryName] = useState(
    existing?.repositoryName ?? '',
  );
  const qualityGateField = useDropdownField(existing?.qualityGateName ?? null);
  // Used instead of the dropdown when the quality gates can't be loaded, so the flow isn't blocked.
  const [qualityGateInput, setQualityGateInput] = useState(
    existing?.qualityGateName ?? '',
  );

  const applicationKey = serviceGroup?.applicationKey ?? '';
  const values = {
    projectKey: (
      keyInput ?? (serviceGroup ? projectKeyPrefix(applicationKey) : '')
    )
      .trim()
      .toLowerCase(),
    projectDescription: projectDescription.trim(),
    repositoryName: repositoryName.trim(),
    qualityGateName: (qualityGates.error
      ? qualityGateInput
      : qualityGateField.value ?? ''
    ).trim(),
  };
  // Locked fields of an existing project are not validated: they are never sent.
  const valid = {
    projectKey:
      Boolean(existing) || isValidProjectKey(values.projectKey, applicationKey),
    projectDescription: isValidProjectDescription(values.projectDescription),
    repositoryName:
      Boolean(existing) || isValidRepositoryName(values.repositoryName),
    qualityGateName: isValidQualityGateName(values.qualityGateName),
  };
  const descriptionChanged =
    values.projectDescription !== existing?.projectDescription;
  const qualityGateChanged =
    values.qualityGateName !== existing?.qualityGateName;

  const [submitState, submit] = useAsyncFn(async () => {
    if (!serviceGroup) throw new Error('Tooling group is still loading.');
    if (existing) {
      // Only changed fields are sent; the service leaves the others as they are.
      await sonarqubeApi.updateProject(serviceGroup.id, existing.projectKey, {
        ...(descriptionChanged
          ? { projectDescription: values.projectDescription }
          : {}),
        ...(qualityGateChanged
          ? { qualityGateName: values.qualityGateName }
          : {}),
      });
    } else {
      await sonarqubeApi.createProject(serviceGroup.id, {
        repositoryName: values.repositoryName,
        projectKey: values.projectKey,
        projectDescription: values.projectDescription,
        qualityGateName: values.qualityGateName,
      });
    }
    onDone();
  }, [
    sonarqubeApi,
    serviceGroup,
    existing,
    descriptionChanged,
    qualityGateChanged,
    values.repositoryName,
    values.projectKey,
    values.projectDescription,
    values.qualityGateName,
  ]);

  /** `step` is 1-based within `sonarQubeProjectSteps`. */
  const isStepValid = (step: number) => {
    switch (step) {
      case 1:
        return valid.projectKey && valid.projectDescription;
      case 2:
        return valid.repositoryName;
      case 3:
        return valid.qualityGateName;
      default:
        return (
          !submitState.loading &&
          (!existing || descriptionChanged || qualityGateChanged)
        );
    }
  };

  return {
    values,
    valid,
    inputs: { projectDescription, repositoryName, qualityGateInput },
    setProjectKey,
    setProjectDescription,
    setRepositoryName,
    setQualityGateInput,
    qualityGates,
    qualityGateField,
    isStepValid,
    submit,
    submitState,
  };
};

type SonarQubeProjectForm = ReturnType<typeof useSonarQubeProjectForm>;

/** Renders `step` (1-based within `sonarQubeProjectSteps`); `existing` locks the key and repository. */
export const SonarQubeProjectStep = ({
  step,
  form,
  serviceGroup,
  existing,
}: {
  step: number;
  form: SonarQubeProjectForm;
  serviceGroup: SonarQubeServiceGroup;
  existing?: ExistingSonarQubeProject;
}) => {
  const { values, valid, inputs, qualityGates, submitState } = form;

  switch (step) {
    case 1:
      return (
        <div className={shared.stepSection}>
          <div className={shared.formRow}>
            <div className={shared.formLabelCol}>
              <span className={shared.formLabel}>
                Project key
                <RequiredStar />
              </span>
              <span className={shared.hintText}>Cannot be changed later.</span>
            </div>
            <div className={shared.formContent}>
              <div className={shared.formQuestionGroup}>
                <span className={shared.formQuestion}>
                  Starts with {projectKeyPrefix(serviceGroup.applicationKey)}
                </span>
                <span className={shared.hintText}>
                  Letters, digits, '-', '_', '.' and ':', at most 400 characters
                </span>
              </div>
              <TextField
                aria-label="Project key"
                className={shared.textFieldControl}
                value={values.projectKey}
                onChange={form.setProjectKey}
                isInvalid={!valid.projectKey}
                isDisabled={Boolean(existing)}
              />
            </div>
          </div>

          <div className={shared.formRow}>
            <span className={shared.formLabel}>
              Description
              <RequiredStar />
            </span>
            <div className={shared.formContent}>
              <div className={shared.formQuestionGroup}>
                <span className={shared.formQuestion}>
                  Shown in SonarQube after the first analysis
                </span>
                <span className={shared.hintText}>
                  At least 4 characters, a single line, without '"' or '\'
                </span>
              </div>
              <TextAreaField
                aria-label="Project description"
                className={shared.textFieldControl}
                value={inputs.projectDescription}
                onChange={form.setProjectDescription}
                isInvalid={!valid.projectDescription}
              />
            </div>
          </div>
        </div>
      );
    case 2:
      return (
        <div className={shared.stepSection}>
          <div className={shared.formRow}>
            <div className={shared.formLabelCol}>
              <span className={shared.formLabel}>
                Repository
                <RequiredStar />
              </span>
              <span className={shared.hintText}>
                You need at least maintain access to the repository.
              </span>
            </div>
            <div className={shared.formContent}>
              <div className={shared.formQuestionGroup}>
                <span className={shared.formQuestion}>
                  Repository name in the GitHub organisation
                </span>
                <span className={shared.hintText}>
                  The name or the GitHub URL
                </span>
              </div>
              <TextField
                aria-label="Repository name"
                className={shared.textFieldControl}
                value={inputs.repositoryName}
                onChange={form.setRepositoryName}
                isInvalid={!valid.repositoryName}
                isDisabled={Boolean(existing)}
              />
            </div>
          </div>
        </div>
      );
    case 3:
      return (
        <div className={shared.stepSection}>
          <div className={shared.formRow}>
            <span className={shared.formLabel}>
              Quality gate
              <RequiredStar />
            </span>
            <div className={shared.formContent}>
              <span className={shared.formQuestion}>
                Quality gate the project is checked against
              </span>
              {qualityGates.error ? (
                <>
                  <span role="alert" className={styles.error}>
                    Could not load quality gates ({qualityGates.error.message}).
                    Type the name instead.
                  </span>
                  <TextField
                    aria-label="Quality gate"
                    className={shared.textFieldControl}
                    value={inputs.qualityGateInput}
                    onChange={form.setQualityGateInput}
                    isInvalid={!valid.qualityGateName}
                  />
                </>
              ) : (
                <DropdownField
                  name="qualityGate"
                  placeholder={
                    qualityGates.loading
                      ? 'Loading quality gates…'
                      : 'Choose quality gate'
                  }
                  options={qualityGates.value?.map(gate => gate.name) ?? []}
                  field={form.qualityGateField}
                  isInvalid={!valid.qualityGateName}
                />
              )}
            </div>
          </div>
        </div>
      );
    default:
      return (
        <div className={shared.stepSection}>
          <div className={shared.summaryBox}>
            <div>
              <h2 className={shared.summaryHeading}>Project</h2>
              <Grid.Root columns="3" gap="14">
                <SummaryValue
                  label="Tooling group"
                  value={serviceGroup.applicationName}
                />
                <SummaryValue label="Project key" value={values.projectKey} />
                <SummaryValue
                  label="Description"
                  value={values.projectDescription}
                  previousValue={existing?.projectDescription}
                />
                <SummaryValue
                  label="Repository"
                  value={values.repositoryName}
                />
                <SummaryValue
                  label="Quality gate"
                  value={values.qualityGateName}
                  previousValue={existing?.qualityGateName}
                />
              </Grid.Root>
            </div>
            <span className={shared.hintText}>
              {existing
                ? "Sets the quality gate and the SONAR_PROJECT_DESCRIPTION repository variable, then runs the repository's SonarQube Analysis workflow. It can take a few minutes before the changes show up, as the analysis has to run first."
                : 'Creates the SonarQube project, binds it to the repository, sets the SONAR_TOKEN and SONAR_HOST_URL repository secrets (overwriting existing ones), and opens a pull request that adds the analysis workflow.'}
            </span>
            {submitState.error && (
              <span role="alert" className={styles.error}>
                {submitState.error.message}
              </span>
            )}
          </div>
        </div>
      );
  }
};

interface CreateSonarQubeProjectPageProps {
  /** Called once the service group loads, so the app shell can show its name in the breadcrumb. */
  onServiceGroupLoaded?: (serviceGroup: SonarQubeServiceGroup) => void;
}

/** Route: /product-teams/:id/tooling-groups/sonarqube/:serviceGroupId/projects/create */
export const CreateSonarQubeProjectPage: FunctionComponent<
  CreateSonarQubeProjectPageProps
> = ({ onServiceGroupLoaded }) => {
  const configApi = useApi(configApiRef);
  const sonarqubeApi = useApi(sonarqubeApiRef);
  const navigate = useNavigate();
  const { id = '', serviceGroupId = '' } = useParams();
  const {
    value: serviceGroup,
    loading,
    error,
  } = useAsync(
    () => sonarqubeApi.getServiceGroup(serviceGroupId),
    [sonarqubeApi, serviceGroupId],
  );

  useEffect(() => {
    if (serviceGroup) {
      onServiceGroupLoaded?.(serviceGroup);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceGroup]);

  const teamPath = `/product-teams/${encodeURIComponent(id)}`;
  const [activeStep, setActiveStep] = useState(1);
  const form = useSonarQubeProjectForm(serviceGroup, () =>
    navigate(
      `${teamPath}/tooling-groups/sonarqube/${encodeURIComponent(serviceGroupId)}`,
    ),
  );

  if (!isPluginEnabled(configApi, 'sonarqube')) {
    return <Navigate to="/" replace />;
  }
  if (loading) {
    return <Progress />;
  }
  if (error) {
    return <ResponseErrorPanel error={error} />;
  }

  return (
    <div className={styles.body}>
      {serviceGroup ? (
        <StepTimeline
          steps={sonarQubeProjectSteps}
          activeStep={activeStep}
          onCancel={() => navigate(teamPath)}
          onPrevious={() => setActiveStep(step => Math.max(step - 1, 1))}
          onNext={() =>
            activeStep === sonarQubeProjectSteps.length
              ? form.submit()
              : setActiveStep(step => step + 1)
          }
          isNextDisabled={!form.isStepValid(activeStep)}
          isSubmitting={form.submitState.loading}
          submitLabel="Create project"
        >
          <SonarQubeProjectStep
            step={activeStep}
            form={form}
            serviceGroup={serviceGroup}
          />
        </StepTimeline>
      ) : (
        <span className={shared.hintText}>Tooling group not found.</span>
      )}
    </div>
  );
};
